import React from 'react';
import {useTheme} from '@mui/material/styles';
import {FormField, Input, SegmentedControl} from 'wobi-web-common/dist/v2/index.js';
import DobField from './DobField';
import IdentityBox from './IdentityBox';
import {fireGtmEvent} from '../utils/marketing.utils';
import {updateTraveler} from '../steps/tabs-contents/initial-details/helpers/Tab4_helpers/travelerUpdates';

function UserIcon({size = 20}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth={2}
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2' />
      <path d='M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8' />
    </svg>
  );
}

function XIcon({size = 15}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth={2}
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M18 6 6 18' /><path d='M6 6l12 12' />
    </svg>
  );
}

function PhoneIcon({size = 19}) {
  const phonePath = [
    'M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07',
    '19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3',
    'a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91',
    'a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7',
    'A2 2 0 0 1 22 16.92z',
  ].join(' ');

  return (
    <svg
      width={size}
      height={size}
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth={2}
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d={phonePath} />
    </svg>
  );
}

/**
 * TravelerCard — single traveler form card matching travelers.html prototype.
 * Travel-only component. Receives Formik handlers via props.
 */
const TravelerCard = ({
  index,
  formikValues,
  formikSetField,
  formikErrors,
  showErrors,
  intl,
  onIdentify,
}) => {
  const theme = useTheme();
  const primary = index === 1;
  const traveler = formikValues.travelers && formikValues.travelers[index - 1];

  const handleFirstNameChange = (value) => {
    formikSetField('firstName' + index, value);
    if (traveler) {
      formikSetField('travelers', updateTraveler(formikValues.travelers, traveler, {firstName: value}));
    }
  };

  const handleLastNameChange = (value) => {
    formikSetField('lastName' + index, value);
    if (traveler) {
      formikSetField('travelers', updateTraveler(formikValues.travelers, traveler, {lastName: value}));
    }
  };

  const handleIdChange = (value) => {
    const clean = value.replace(/[^0-9]/g, '').slice(0, 9);
    formikSetField('ID' + index, clean);
    if (traveler) {
      formikSetField('travelers', updateTraveler(formikValues.travelers, traveler, {ID: clean}));
    }
  };

  const handleDobChange = (isoValue) => {
    formikSetField('dob' + index, isoValue || '');
    if (traveler) {
      formikSetField('travelers', updateTraveler(formikValues.travelers, traveler, {dob: isoValue || ''}));
    }
  };

  const handleGenderChange = (value) => {
    formikSetField('gender' + index, value);
    if (traveler) {
      formikSetField('travelers', updateTraveler(formikValues.travelers, traveler, {gender: value}));
    }
  };

  const handlePhoneChange = (value) => {
    const clean = value.replace(/[^0-9]/g, '').slice(0, 10);
    formikSetField('phone1', clean);
  };

  // Error detection
  const firstNameErr = showErrors && formikErrors['firstName' + index];
  const lastNameErr = showErrors && formikErrors['lastName' + index];
  const dobErr = showErrors && formikErrors['dob' + index];
  const idErr = showErrors && formikErrors['ID' + index];
  const genderErr = showErrors && formikErrors['gender' + index];
  const phoneErr = primary && showErrors && formikErrors.phone1;

  return (
    <div
      className={'traveler-card' + (primary ? ' traveler-card-primary' : '')}
      style={{
        '--traveler-surface': theme.palette.background.paper,
        '--traveler-border': theme.palette.divider,
        '--traveler-shadow': theme.shadows[4],
        '--traveler-icon-surface': theme.palette.action.disabledBackground,
        '--traveler-heading': theme.palette.secondary.main,
        '--traveler-copy-muted': theme.palette.text.secondary,
        '--traveler-error': theme.palette.error.main,
        '--traveler-card-radius': 'var(--wobi-v2-radius-2xl)',
        '--traveler-title-size': theme.typography.pxToRem(19),
        '--traveler-label-size': theme.typography.pxToRem(12.5),
        '--traveler-action-size': theme.typography.pxToRem(13.5),
        '--traveler-error-size': theme.typography.pxToRem(12),
        '--traveler-weight-black': theme.typography.fontWeightBlack,
        '--traveler-weight-bold': theme.typography.fontWeightBold,
        '--traveler-weight-semibold': theme.typography.fontWeightSemiBold,
      }}
      name={'policyholdDetails-' + index + '-div'}
      id={'policyholdDetails-' + index + '-div'}
    >
      {/* Header */}
      {primary ? (
        <div className='traveler-card-header'>
          <div className='traveler-card-identity'>
            <span className='traveler-card-icon'>
              <UserIcon size={20} />
            </span>
            <div className='traveler-card-title'>
              {intl.formatMessage({id: 'step1.tab2.DoBTitle_primary'})}
            </div>
          </div>
          <div className='traveler-card-identify'>
            <IdentityBox onIdentify={onIdentify} />
          </div>
        </div>
      ) : (
        <div className='traveler-card-header traveler-card-header-secondary'>
          <span className='traveler-card-icon'>
            <UserIcon size={20} />
          </span>
          <div className='traveler-card-title'>
            {intl.formatMessage({id: 'step1.tab2.DoBTitle'}) + ' ' + index}
          </div>
          <button
            type='button'
            onClick={() => handleRemoveTraveler()}
            className='traveler-card-remove'
            data-testid={`remove-traveler-${index}`}
          >
            <XIcon size={15} /> {intl.formatMessage({id: 'step1.tab2.removeTraveler'})}
          </button>
        </div>
      )}

      {/* Fields grid */}
  <div className='traveler-card-fields'>
        <FormField
          label={intl.formatMessage({id: 'step1.tab2.firstName'})}
          htmlFor={'firstName' + index}
          error={firstNameErr || undefined}
        >
          <Input
            size='lg'
            id={'firstName' + index}
            data-testid={`first-name-${index}`}
            value={formikValues['firstName' + index] || ''}
            onChange={(e) => handleFirstNameChange(e.target.value)}
            placeholder={intl.formatMessage({id: 'travelerCard.firstNamePlaceholder'})}
            invalid={!!firstNameErr}
          />
        </FormField>

        <FormField
          label={intl.formatMessage({id: 'step1.tab2.lastName'})}
          htmlFor={'lastName' + index}
          error={lastNameErr || undefined}
        >
          <Input
            size='lg'
            id={'lastName' + index}
            data-testid={`last-name-${index}`}
            value={formikValues['lastName' + index] || ''}
            onChange={(e) => handleLastNameChange(e.target.value)}
            placeholder={intl.formatMessage({id: 'travelerCard.lastNamePlaceholder'})}
            invalid={!!lastNameErr}
          />
        </FormField>

        <DobField
          value={formikValues['dob' + index] || ''}
          onChange={handleDobChange}
          error={!!dobErr}
          errorMessage={dobErr}
          label={intl.formatMessage({id: 'step1.tab2.dob'})}
        />

        <FormField
          label={intl.formatMessage({id: 'step1.tab2.ID'})}
          htmlFor={'ID' + index}
          error={idErr || undefined}
        >
          <Input
            size='lg'
            id={'ID' + index}
            data-testid={`id-${index}`}
            value={formikValues['ID' + index] || ''}
            onChange={(e) => handleIdChange(e.target.value)}
            placeholder={intl.formatMessage({id: 'travelerCard.idPlaceholder'})}
            inputMode='numeric'
            invalid={!!idErr}
          />
        </FormField>

        {primary && (
          <FormField
            label={intl.formatMessage({id: 'step1.tab2.phone'})}
            htmlFor='phone1'
            error={phoneErr || undefined}
          >
            <Input
              size='lg'
              id='phone1'
              data-testid='main-phone-number'
              value={formikValues.phone1 || ''}
              onChange={(e) => handlePhoneChange(e.target.value)}
              placeholder='05X-XXXXXXX'
              inputMode='tel'
              invalid={!!phoneErr}
              iconStart={<PhoneIcon size={19} />}
              inputStyle={{direction: 'ltr', textAlign: 'right'}}
            />
          </FormField>
        )}

        <div className='traveler-gender-field'>
          <div className='traveler-field-label'>
            {intl.formatMessage({id: 'step1.tab2.gender'})}
          </div>
          <SegmentedControl
            className='traveler-gender-control'
            ariaLabel={intl.formatMessage({id: 'step1.tab2.gender'})}
            value={formikValues['gender' + index]}
            options={[
              {value: '1', label: intl.formatMessage({id: 'male'})},
              {value: '2', label: intl.formatMessage({id: 'female'})},
            ]}
            onChange={handleGenderChange}
            invalid={!!genderErr}
            size='lg'
          />
          {genderErr && (
            <div className='traveler-field-error'>
              {genderErr}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  function handleRemoveTraveler() {
    fireGtmEvent('TravelRemovePassanger');

    const newTravelers = formikValues.travelers.filter(
      (_, travelerIdx) => travelerIdx + 1 !== index,
    );
    const newPolicyholdNumber = newTravelers.length;
    formikSetField('policyholdNum', newPolicyholdNumber);
    formikSetField('travelers', newTravelers);

    // Reorder flat Formik fields
    for (let i = 0; i < 10; i++) {
      const t = newTravelers[i] || {};
      formikSetField('ID' + (i + 1), t.ID || '');
      formikSetField('firstName' + (i + 1), t.firstName || '');
      formikSetField('lastName' + (i + 1), t.lastName || '');
      formikSetField('dob' + (i + 1), t.dob || '');
      formikSetField('gender' + (i + 1), t.gender || '');
    }
    formikSetField('policyholdNum', newTravelers.length);
  }
};

export default TravelerCard;
